import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

interface TableProps extends React.ComponentProps<"table"> {
  wrapperClassName?: string;
  noWrapper?: boolean;
}

function Table({
  className,
  wrapperClassName,
  noWrapper = false,
  ...props
}: TableProps) {
  const table = (
    <table
      data-slot="table"
      className={cn(
        "w-full caption-bottom text-[length:var(--table-font-size)]",
        className,
      )}
      {...props}
    />
  );

  if (noWrapper) return table;

  return (
    <div className={cn("relative w-full overflow-auto", wrapperClassName)}>
      {table}
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("bg-surface-2 [&_tr]:border-b", className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t border-border bg-surface-2 font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b border-border transition-colors hover:bg-surface-2 data-[state=selected]:bg-action-tint data-[state=selected]:hover:bg-action-tint data-[state=selected]:shadow-[inset_3px_0_0_var(--action)]",
        className,
      )}
      {...props}
    />
  );
}

const tableHeadVariants = cva(
  "h-[var(--table-header-height)] px-[var(--table-cell-px)] py-[var(--table-header-py)] text-left align-middle font-medium text-fg-subtle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
  {
    variants: {
      tone: {
        label: "tabular-nums text-[length:var(--table-header-font-size)] uppercase tracking-[0.05em]",
        plain: "text-xs text-muted-foreground",
      },
    },
    defaultVariants: {
      tone: "label",
    },
  },
);

function TableHead({
  className,
  tone,
  ...props
}: React.ComponentProps<"th"> & VariantProps<typeof tableHeadVariants>) {
  return (
    <th
      data-slot="table-head"
      className={cn(tableHeadVariants({ tone }), className)}
      {...props}
    />
  );
}

const tableCellVariants = cva(
  "h-[var(--table-row-height)] px-[var(--table-cell-px)] py-[var(--table-cell-py)] align-middle tabular-nums [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
  {
    variants: {
      emphasis: {
        muted: "text-muted-foreground",
        default: "",
        medium: "font-medium",
        strong: "font-semibold",
      },
      mono: {
        true: "tabular-nums",
        false: "",
      },
    },
    defaultVariants: {
      emphasis: "default",
      mono: false,
    },
  },
);

function TableCell({
  className,
  emphasis,
  mono,
  ...props
}: React.ComponentProps<"td"> & VariantProps<typeof tableCellVariants>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(tableCellVariants({ emphasis, mono }), className)}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  tableCellVariants,
};
